import { useContext, useRoute, useRouter } from "@nuxtjs/composition-api";
import { useAuthStore } from "../store";
import {
  applyPriceOfferLoginOverride,
  resolvePostLoginPath,
} from "../postLogin";
import { useCheckMismatch } from "./useCheckMismatch";
import { useCookieManager } from "./useCookieManager";
import { useUserStore } from "~/stores/user";
import { useSyncLocalFavourites } from "~/composables/favourite/useSyncLocalFavourites";

/**
 * Shared post-login flow: sets token, loads user, navigates.
 * Used by both email/password login and OAuth login.
 */
export function usePostLogin() {
  const authStore = useAuthStore();
  const userStore = useUserStore();
  const checkMismatch = useCheckMismatch();
  const syncLocalFavourites = useSyncLocalFavourites();
  const { localePath, $cookie, $helpers } = useContext();
  const route = useRoute();
  const router = useRouter();

  async function loginToOneSignal(resp) {
    if (
      window?.OneSignal &&
      typeof window?.OneSignal.login === "function" &&
      resp.isMate
    ) {
      await window?.OneSignal.login(resp.personId);
    }
  }

  function loadNextPage() {
    router.push(
      resolvePostLoginPath({
        query: route.value.query,
        me: userStore.me,
        localePath,
        router,
      })
    );
  }

  /**
   * @param {Object} params
   * @param {string} params.token - JWT token
   * @param {string} params.personId - Person ID for mismatch check
   * @param {boolean} [params.isMate] - Whether user is a mate (for OneSignal)
   * @param {Object} [options]
   * @param {boolean} [options.navigate=true] - Navigate to the next page after
   *   login. Set false to stay on the current page (e.g. when logging in from a
   *   dialog).
   */
  return async function postLogin(
    { token, personId, isMate },
    { navigate = true } = {}
  ) {
    checkMismatch(personId, token);

    await authStore.loginWithToken(token);

    applyPriceOfferLoginOverride({
      query: route.value.query,
      userStore,
      $cookie,
      $helpers,
    });

    userStore.updateLastPerson();

    await loginToOneSignal({ personId, isMate });

    // Best-effort, fire-and-forget migration of legacy localStorage favourites.
    syncLocalFavourites();

    if (navigate) {
      loadNextPage();
    }
  };
}

export function useLogin() {
  const { $mutate, $helpers, $cookie } = useContext();
  const cookieManager = useCookieManager({ $helpers, $cookie });
  const postLogin = usePostLogin();

  async function loginRequest(email, password) {
    const LOGIN_MUTATION = await import("../mutations/login.graphql");
    return $mutate(LOGIN_MUTATION, {
      email,
      password,
      campaignsCookie: cookieManager.getCampaignsCookie(),
    });
  }

  async function firstLoginRequest(credentials) {
    const FIRST_LOGIN_MUTATION = await import(
      "../mutations/firstLoginMutation.graphql"
    );
    return $mutate(FIRST_LOGIN_MUTATION, {
      email: credentials.email,
      newPassword: credentials.password,
      emailConfirmationCode: credentials.emailConfirmationCode,
      campaignsCookie: cookieManager.getCampaignsCookie(),
    });
  }

  /**
   *
   * @param {Object} credentials - The credentials to login
   * @param {string} credentials.email
   * @param {string} credentials.password
   * @param {string} [credentials.emailConfirmationCode] - Only in case of first login
   * @param {Object} [options]
   * @param {boolean} [options.navigate=true] - Navigate to the next page after
   *   login. Set false to stay on the current page (e.g. dialog login).
   */
  return async function login(credentials, { navigate = true } = {}) {
    let resp;
    if (credentials.emailConfirmationCode) {
      resp = await firstLoginRequest(credentials);
    } else {
      resp = await loginRequest(credentials.email, credentials.password);
    }

    await postLogin(
      {
        token: resp.token,
        personId: resp.personId,
        isMate: resp.isMate,
      },
      { navigate }
    );
  };
}
